import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import {
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Tooltip,
  Chip,
  FormControl,
  Input,
  Select as SelectMUI,
  Switch,
  FormControlLabel,
  TextField as TextFieldMaterialUI,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { TextField, Switch as SwitchFormik } from "formik-mui";
import { useSelector, useDispatch } from "react-redux";
import MaterialTable from "@material-table/core";
import { useSnackbar } from "notistack";
import { Autocomplete } from "@mui/lab";
import { ThemeProvider, createTheme, alpha, darken, lighten, useTheme } from "@mui/material/styles";

import {
  addLayoutFilter,
  deleteLayoutFilter,
  editLayoutFilter,
  getByLayoutId,
} from "../../../../store/ducks/duckLayoutFilter";
import { getTabs } from "../../../../store/ducks/duckTab";
import { getFilters } from "../../../../store/ducks/duckFilter";
import { getSources } from "../../../../store/ducks/duckSource";
import { useStylesLayout } from "../../../../styles/Layout";
import { addOrEditLayout } from "../../../../store/ducks/duckLayout";
import { getMenuItems } from "../../../../store/ducks/duckMenuItem";
import { useUser } from "../../../../auth/UserProvider";
import { BorderLinearProgress } from "../../../../components/LoadingComponent";
import FormikAutocompleteMenuItem from "../../../../components/FormikAutocompleteMenuItem";

const AUTH_OPTIONS = ["Sessions", "Normal Auth", "SSO"];

const LayoutForm = (props) => {
  const { initialValues, isEdit, handleClose, open } = props;
  const [isDialogEntered, setIsDialogEntered] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const classes = useStylesLayout();
  const { isAdmin } = useUser();

  const dataTabs = useSelector((state) => {
    let data = state.tab.data.sort((a, b) => (a.label > b.label ? 1 : -1));
    let dataL = [];

    data.forEach((x) => {
      let tabDataEntity = { id: x.id, label: x.label };

      dataL.push(tabDataEntity);
    });
    let hash = {};

    dataL = dataL.filter((o) => (hash[o.label] ? false : (hash[o.label] = true)));

    return dataL;
  });

  const dataSources = useSelector((state) => {
    let data = state.source.data.sort((a, b) => (a.name > b.name ? 1 : -1));
    let dataL = [];

    data.forEach((x) => {
      let sourceDataEntity = { id: x.id, label: x.name };

      dataL.push(sourceDataEntity);
    });

    return dataL;
  });

  const getDataSourcesId = () => dataSources.map((x) => x.id);
  const getDataSourcesLabel = (id) => dataSources.find((x) => x.id === id)?.label;
  const getDataTabId = () => dataTabs.map((x) => x.id);
  const getDataTabLabel = (id) => dataTabs.find((x) => x.id === id)?.label;

  const layoutsFiltersLoading = useSelector((state) => state.layoutFilter.isLoading);
  const layoutFilter = useSelector((state) => state.layoutFilter.layoutFiltersSelected);
  const filters = useSelector((state) => state.filter.data);
  const { enqueueSnackbar } = useSnackbar();

  const dataFilters = useSelector((state) => {
    let data = state.filter.data;
    let dataL = {};

    data.forEach((x) => {
      dataL[x.id] = x.name;
    });

    return dataL;
  });

  const dispatch = useDispatch();
  const materialTableTheme = useMemo(() => {
    const theme = createTheme();

    // Compatibility shim for @material-table/core pulling MUI internals that expect these helpers on theme.
    theme.alpha = alpha;
    theme.darken = darken;
    theme.lighten = lighten;

    return theme;
  }, []);

  useEffect(() => {
    dispatch(getByLayoutId(initialValues.id));
    dispatch(getFilters());
    dispatch(getTabs());
    dispatch(getSources());
    dispatch(getMenuItems());
  }, [dispatch, initialValues.id]);

  useEffect(() => {
    if (!open) {
      setIsDialogEntered(false);
    }
  }, [open]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validate={(values) => {
          let errors = {};

          if (!values.name) {
            errors.name = "Required";
          }

          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          values = {
            ...values,
            layoutId: values.layoutId === 0 ? null : values.layoutId,
          };

          let message = values.id > 0 ? "Edited" : "Added";

          return dispatch(addOrEditLayout(values, () => setSubmitting(false))).then(() => {
            handleClose();
            enqueueSnackbar(`${message} '${values.name}' Layout Successfully`, {
              variant: "success",
            });
          });
        }}
      >
        {({ submitForm, isSubmitting, values, setFieldValue }) => (
          <Form>
            <Dialog
              fullWidth
              aria-labelledby="form-dialog-title"
              fullScreen={isSmallScreen}
              maxWidth="lg"
              open={open}
              onClose={handleClose}
              scroll="paper"
              sx={{
                "& .MuiDialogTitle-root": {
                  py: 1,
                  fontSize: "1rem",
                },
                "& .MuiDialogContent-root": {
                  fontSize: "0.82rem",
                },
                "& .MuiInputBase-root": {
                  fontSize: "0.82rem",
                },
                "& .MuiFormLabel-root": {
                  fontSize: "0.78rem",
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "0.74rem",
                },
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.78rem",
                },
                "& .MuiButton-root": {
                  fontSize: "0.74rem",
                  minHeight: 30,
                  py: 0.4,
                  px: 1.4,
                },
                "& .MuiChip-label": {
                  fontSize: "0.72rem",
                },
                "& .MuiTableCell-root": {
                  fontSize: "0.78rem",
                },
              }}
              PaperProps={{
                sx: {
                  width: { xs: "100%", sm: "94vw", md: "90vw", lg: "84vw" },
                  maxWidth: { xs: "100%", sm: "94vw", md: "90vw", lg: "84vw" },
                  height: { xs: "100dvh", sm: "92vh" },
                  maxHeight: { xs: "100dvh", sm: "92vh" },
                  display: "flex",
                },
              }}
              TransitionProps={{
                onEntered: () => setIsDialogEntered(true),
                onExit: () => setIsDialogEntered(false),
              }}
            >
              {isSubmitting && <BorderLinearProgress />}
              <DialogTitle id="form-dialog-title">
                {isEdit && initialValues?.id !== 0
                  ? "Edit"
                  : !isEdit && initialValues?.id !== 0
                    ? "Details"
                    : "Add"}
                {initialValues.deleted && isAdmin && (
                  <>
                    <Tooltip
                      key={`Helper-deleted-admin-tooltip`}
                      title={values.deleted ? "Enable" : "Disable"}
                    >
                      <FormControlLabel
                        control={
                          <Field
                            color="primary"
                            component={SwitchFormik}
                            name="deleted"
                            type="checkbox"
                          />
                        }
                        label="Deleted"
                        labelPlacement="start"
                        style={{ marginRight: 0, marginLeft: "80%" }}
                      />
                    </Tooltip>
                  </>
                )}
              </DialogTitle>
              <DialogContent
                sx={{
                  overflowY: "auto",
                  overflowX: "hidden",
                  flex: 1,
                  py: 1,
                  px: { xs: 1, sm: 1.5 },
                }}
              >
                <Grid container spacing={1}>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Field
                        fullWidth
                        component={TextField}
                        label="Name"
                        margin="dense"
                        name="name"
                        type="text"
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <Field
                        component={FormikAutocompleteMenuItem}
                        disabled={values.deleted}
                        margin="dense"
                        name="menuItemId"
                        style={{ width: "100%", height: "100%" }}
                        textFieldProps={{
                          fullWidth: true,
                          margin: "dense",
                          variant: "outlined",
                          label: "Menu Item",
                        }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item style={{ padding: 10 }} xs={4}>
                      <Grid
                        container
                        alignContent="center"
                        alignItems="center"
                        justify="center"
                        spacing={1}
                      >
                        <Grid item xs={6}>
                          <Tooltip
                            title={`If the layout is for testing, the report will be
                              searched on the development servers.`}
                          >
                            <FormControlLabel
                              fullWidth
                              control={
                                <Switch
                                  checked={values.testing}
                                  color="primary"
                                  name="testing"
                                  onChange={() => setFieldValue("testing", !values.testing)}
                                />
                              }
                              label="Testing"
                            />
                          </Tooltip>
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Field
                                color="primary"
                                component={SwitchFormik}
                                name="isEconomics"
                                type="checkbox"
                              />
                            }
                            label="Economics"
                            labelPlacement="end"
                            style={{ padding: "2px 0 0 4px", marginLeft: 0 }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    {" "}
                    {/* initialEditValue */}
                    <Grid item xs={12}>
                      <Field
                        fullWidth
                        multiline
                        component={TextField}
                        label="Description"
                        margin="dense"
                        name="description"
                        rows={2}
                        type="text"
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                  {initialValues.id !== 0 && !values.deleted && isDialogEntered && (
                    <Grid
                      container
                      spacing={1}
                      sx={{ backgroundColor: "rgba(1, 1, 1, 0.05)", m: 0.5 }}
                    >
                      <Grid item xs={1} />
                      <Grid item xs={10}>
                        <ThemeProvider theme={materialTableTheme}>
                          <MaterialTable
                            columns={[
                            {
                              title: "LayoutId",
                              field: "layoutId",
                              hidden: true,
                            },
                            {
                              title: "SSO",
                              field: "sso",
                              hidden: true,
                            },
                            {
                              title: "Format Number",
                              field: "requireFormatNumber",
                              type: "boolean",
                            },
                            {
                              title: "Tab",
                              field: "tabId",
                              type: "numeric",
                              editComponent: (props) => (
                                <Autocomplete
                                  key={`Autocomplete - ${props.rowData.tabId}`}
                                  getOptionLabel={(options) => getDataTabLabel(options)}
                                  id="TabAutocomplete"
                                  options={getDataTabId()}
                                  renderInput={(params) => {
                                    return (
                                      <TextFieldMaterialUI
                                        {...params}
                                        key={`TextField - ${props.id} - ${props.label}`}
                                        label={props.label}
                                        variant="outlined"
                                      />
                                    );
                                  }}
                                  size="small"
                                  value={props.rowData.tabId}
                                  // getOptionSelected={(option, value) => option.id === value}
                                  onChange={(e, value) => props.onChange(value)}
                                />
                              ),
                              render: (rowData) => (
                                <> {dataTabs.find((d) => d.id === rowData.tabId)?.label}</>
                              ),
                            },
                            {
                              title: "Order",
                              field: "orderTab",
                              type: "numeric",
                            },
                            {
                              title: "Auth",
                              field: "requiredNormalAuth",
                              initialEditValue: (rowData) =>
                                rowData.requiredNormalAuth ? 1 : rowData.sso ? 2 : 0,
                              render: (rowData) => (
                                <>
                                  {rowData?.requiredNormalAuth && "Normal Auth"}
                                  {rowData?.sso && "SSO"}
                                  {!rowData?.sso && !rowData?.requiredNormalAuth && "Sessions"}
                                </>
                              ),
                              editComponent: (x) => {
                                return (
                                  <FormControl variant="outlined">
                                    <SelectMUI
                                      defaultValue={
                                        x.rowData.requiredNormalAuth
                                          ? AUTH_OPTIONS[1]
                                          : x.rowData.sso
                                            ? AUTH_OPTIONS[2]
                                            : AUTH_OPTIONS[0]
                                      }
                                      id="simple-select-auth"
                                      input={<Input id="select-multiple-chip" />}
                                      labelId="simple-select-auth"
                                      renderValue={(selected) => <>{selected}</>}
                                      variant="outlined"
                                      onChange={(e) => {
                                        let auth = e.target.value;
                                        let requiredNormalAuth = false;
                                        let sso = false;

                                        switch (auth) {
                                          case "SSO":
                                            sso = true;
                                            break;
                                          case "Normal Auth":
                                            requiredNormalAuth = true;
                                            break;
                                          case "Sessions":
                                            sso = false;
                                            requiredNormalAuth = false;
                                            break;
                                          default:
                                            break;
                                        }

                                        return x.onRowDataChange({
                                          ...x.rowData,
                                          requiredNormalAuth,
                                          sso,
                                        });
                                      }}
                                    >
                                      {AUTH_OPTIONS.map((auth, i) => {
                                        return (
                                          <MenuItem
                                            key={`MenuItem-Auth-${i}`}
                                            selected
                                            value={auth}
                                          >
                                            {auth}
                                          </MenuItem>
                                        );
                                      })}
                                    </SelectMUI>
                                  </FormControl>
                                );
                              },
                            },
                            {
                              title: "Filters",
                              field: "layoutFilterFilters",
                              lookup: dataFilters,
                              render: (rowData) => {
                                if (
                                  !rowData.layoutFilterFilters ||
                                  rowData.layoutFilterFilters.length === 0
                                )
                                  return;

                                return (
                                  <>
                                    {rowData.layoutFilterFilters.map((row) => {
                                      if (!row) return null;

                                      return (
                                        <Tooltip
                                          key={`Tooltip- Filters ${row.filterId}`}
                                          title={
                                            filters?.find((x) => x.id === row.filterId)?.name ?? ""
                                          }
                                        >
                                          <Chip
                                            key={`Chip- Filters ${row.filterId}`}
                                            className={classes.chipLayouts}
                                            label={
                                              filters
                                                ?.find((x) => x.id === row.filterId)
                                                ?.name.slice(0, 1) ?? "None"
                                            }
                                            size="small"
                                            variant="outlined"
                                          />
                                        </Tooltip>
                                      );
                                    })}
                                  </>
                                );
                              },
                              editComponent: (x) => {
                                return (
                                  <FormControl variant="outlined">
                                    <SelectMUI
                                      multiple
                                      disabled={false}
                                      id="demo-mutiple-chip"
                                      input={<Input id="select-multiple-chip" />}
                                      labelId="demo-mutiple-chip-label"
                                      renderValue={(selected) => (
                                        <div
                                          key={`Container-Chip-Select`}
                                          style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          {selected.map((value) => {
                                            let valTemp;

                                            if (Number.isFinite(value)) {
                                              valTemp = value;
                                            } else {
                                              //Its a object from state;
                                              valTemp = value?.filterId;
                                            }

                                            return (
                                              <Chip
                                                key={`Filter-Filter-${valTemp}`}
                                                label={
                                                  filters?.find((x) => x.id === valTemp)?.name ??
                                                  "None"
                                                }
                                                style={{
                                                  margin: 2,
                                                }}
                                              />
                                            );
                                          })}
                                        </div>
                                      )}
                                      value={x.value || []}
                                      variant="outlined"
                                      onChange={(e) => {
                                        let layoutfilterfiltersTemp = e.target.value.map((x) => {
                                          if (Number.isFinite(x)) {
                                            return {
                                              filterId: x,
                                              layoutFilterId: 0,
                                            };
                                          }

                                          return x;
                                        });

                                        if (
                                          e.target.value.some(
                                            (x) => x?.filterId == null || x === null,
                                          )
                                        ) {
                                          layoutfilterfiltersTemp = null;
                                        }

                                        return x.onRowDataChange({
                                          ...x.rowData,
                                          layoutFilterFilters: layoutfilterfiltersTemp,
                                        });
                                      }}
                                    >
                                      <MenuItem
                                        key={`MenuItem-Filter-None`}
                                        value={{ filterId: null, layoutFilterId: x.rowData.id }}
                                      >
                                        {"None"}
                                      </MenuItem>
                                      {filters?.map((f, i) => {
                                        let exist = x.value?.find((v) => v?.filterId === f.id);
                                        let value = exist
                                          ? exist
                                          : f.value === null
                                            ? null
                                            : {
                                                layoutFilterId: 0,
                                                filterId: f.id,
                                              };

                                        return (
                                          <MenuItem
                                            key={`MenuItem-Filter-${i}`}
                                            selected
                                            value={value}
                                          >
                                            {`${f.name},  Id: ${f.id}`}
                                          </MenuItem>
                                        );
                                      })}
                                    </SelectMUI>
                                  </FormControl>
                                );
                              },
                            },
                            { title: "", field: "id", hidden: true },
                            {
                              title: "Source",
                              field: "sourceId",
                              type: "numeric",
                              editComponent: (props) => (
                                <Autocomplete
                                  key={`Autocomplete Source- ${props.value} `}
                                  getOptionLabel={(option) => getDataSourcesLabel(option)}
                                  id="SourceAutoComplete"
                                  options={getDataSourcesId()}
                                  renderInput={(params) => {
                                    return (
                                      <TextFieldMaterialUI
                                        {...params}
                                        key={`TextField Source- ${props.value}`}
                                        variant="outlined"
                                      />
                                    );
                                  }}
                                  size="small"
                                  value={props.rowData.sourceId}
                                  onChange={(event, newValue) => {
                                    props.onChange(newValue);
                                  }}
                                />
                              ),
                              render: (rowData) => {
                                let renderValue = dataSources.find(
                                  (d) => d.id === rowData.sourceId,
                                )?.label;

                                if (renderValue) {
                                  renderValue = renderValue.slice(0, 15);

                                  return <>{renderValue}</>;
                                }

                                return <></>;
                              },
                            },
                          ]}
                            components={{
                              Container: (props) => <Paper {...props} elevation={0} />,
                            }}
                            data={layoutFilter || []}
                            editable={{
                            onRowAdd: (newData) =>
                              new Promise((resolve, _reject) => {
                                dispatch(
                                  addLayoutFilter({
                                    ...newData,
                                    layoutId: initialValues.id,
                                  }),
                                );
                                resolve();
                              }),
                            onRowUpdate: (newData, oldData) =>
                              new Promise((resolve, _reject) => {
                                let layoutfilterfiltersTemp = newData.layoutFilterFilters?.map(
                                  (x) => {
                                    if (Number.isFinite(x)) {
                                      return {
                                        filterId: x,
                                        layoutFilterId: 0,
                                      };
                                    }

                                    return x;
                                  },
                                );

                                layoutfilterfiltersTemp = layoutfilterfiltersTemp ?? [
                                  { filterId: null, layoutFilterId: oldData.id },
                                ];
                                dispatch(
                                  editLayoutFilter(
                                    {
                                      ...newData,
                                      layoutFilterFilters: layoutfilterfiltersTemp,
                                    },
                                    oldData.id,
                                  ),
                                );
                                resolve();
                              }),
                            onRowDelete: (oldData) =>
                              new Promise((resolve, _reject) => {
                                dispatch(deleteLayoutFilter(oldData));
                                resolve();
                              }),
                          }}
                            isLoading={layoutsFiltersLoading}
                            options={{
                              actionsColumnIndex: -1,
                              pageSize: 5,
                              pageSizeOptions: [5, 10],
                              search: false,
                              maxBodyHeight: 320,
                              padding: "dense",
                              toolbar: false,
                            }}
                            title="Tabs"
                          />
                        </ThemeProvider>
                      </Grid>
                      <Grid item xs={1} />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 2, py: 1 }}>
                <Button color="primary" size="small" variant="contained" onClick={handleClose}>
                  Close
                </Button>
                {isEdit && (
                  <Button color="primary" size="small" variant="contained" onClick={submitForm}>
                    Save
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default LayoutForm;
